// Central catalog of backend endpoint URL builders, exposed as $api.routes.
// All HTTP transfer goes through usePbApi (composables/api.ts) — the single fetch path.
import type { ExposureStatsWindow } from '~/types/person/exposure-stats';

export default defineNuxtPlugin(() => {
    return {
        provide: {
            api: {
                routes: {
                    // The signed-in member's household links, both ways: who manages (or asked to
                    // manage) their account, and whose accounts they manage (or asked to). Every
                    // answer is keyed by the link, and ending one is the same DELETE from either side.
                    household: {
                        list: () => `/me/household`,
                        accept: (linkId: number) => `/me/household/${linkId}/accept`,
                        decline: (linkId: number) => `/me/household/${linkId}/decline`,
                        resend: (linkId: number) => `/me/household/${linkId}/resend`,
                        end: (linkId: number) => `/me/household/${linkId}`,
                    },
                    me: {
                        getMe: () => `/me`,
                        deleteAccount: () => `/me`,
                        // Every sign-in session of the member ends, on every device: what a
                        // sign-out asks. One session from Settings → Security is
                        // `loginActivity.revoke`.
                        revokeTokens: () => `/me/tokens`,
                        emails: {
                            sendVerification: () => `/me/emails/verify`,
                        },

                        mfa: {
                            get: () => `/me/mfa`,
                            enable: () => `/me/mfa`,
                            disable: () => `/me/mfa`,
                        },
                        passkeys: {
                            get: () => `/me/passkeys`,
                            delete: (id: number) => `/me/passkeys/${id}`,
                        },
                        loginActivity: {
                            get: () => `/me/login-activity`,
                            revoke: (tokenId: string) => `/me/login-activity/${tokenId}`,
                        },
                        // The assistants the member has connected over OAuth. Disconnecting one
                        // revokes that client's tokens only — the member's own sessions on
                        // /me/login-activity are a separate act on a separate route. A client id
                        // is the UUID the API issues for the connector, so it is one path segment
                        // as it stands.
                        connections: {
                            get: () => `/me/connections`,
                            disconnect: (clientId: string) => `/me/connections/${clientId}`,
                        },
                        // Companies that added the member by an address they already held and
                        // wait on their answer. Each answer is the signed-in member's own, keyed by
                        // the company that asked.
                        employmentInvites: {
                            list: () => `/me/employment-invites`,
                            accept: (companyId: number) => `/me/employment-invites/${companyId}/accept`,
                            decline: (companyId: number) => `/me/employment-invites/${companyId}/decline`,
                        },

                    },
                    companies: {
                        deletion_guide: (company_id: number): string => `public/companies/${company_id}/guide`,
                    },
                    people: {
                        person: (person_id: number) => `/people/${person_id}`,
                        /**
                         * GET /v3/people/{person_id}/scan-progress
                         * How much of this person's broker sweep has finished:
                         * `{running, checked, total, percent}`.
                         */
                        scanProgress: (person_id: number) => `/people/${person_id}/scan-progress`,
                        billing: {
                            creditBalance: (person_id: number) => `/people/${person_id}/financial/credit-balance`,
                            giftCertificates: {
                                purchase: (person_id: number) => `/people/${person_id}/financial/gift-certificates`,
                                apply: (person_id: number) => `/people/${person_id}/financial/gift-certificates/apply`,
                                // A code's check symbol can be `$` or `=`, so it travels encoded.
                                email: (person_id: number, code: string) =>
                                    `/people/${person_id}/financial/gift-certificates/${encodeURIComponent(code)}/email`,
                            },
                            invoices: {
                                get: (person_id: number, page: number = 1, perPage: number = 10) =>
                                    `/people/${person_id}/financial/invoices?page=${page}&per_page=${perPage}`,
                                download: (person_id: number, invoice_id: string) =>
                                    `/people/${person_id}/financial/invoices/${invoice_id}`,
                            },
                        },
                        exposures: {
                            exposure: (person_id: number, exposure_id: string) => `/people/${person_id}/exposures/${exposure_id}`,
                            exposure_history: (person_id: number, exposure_id: string) =>
                                `/people/${person_id}/exposures/${exposure_id}/history`,
                            company_history: (person_id: number, company_id: number) =>
                                `/people/${person_id}/exposures/company/${company_id}/history`,
                            // The one read of the member's exposure statistics, for a Digital Footprint window the
                            // endpoint offers: it refuses any other with a 422.
                            stats: (person_id: number, months: ExposureStatsWindow) =>
                                `/people/${person_id}/exposures/stats?months=${months}`,
                            recent: (person_id: number, limit: number = 6) =>
                                `/people/${person_id}/exposures/recent?limit=${limit}`,
                            data_exposed: (person_id: number) => `/people/${person_id}/exposures/data-exposed/v2`,
                            detail: (person_id: number) => `/people/${person_id}/exposures/detail`,
                            list: (person_id: number, limit: number = 50) => `/people/${person_id}/exposures?limit=${limit}`,
                            suppressions: {
                                get_suppressions: (person_id: number) => `/people/${person_id}/exposures/suppressions`,
                                update_suppressions: (person_id: number) => `/people/${person_id}/exposures/suppressions`,
                                remove_suppression: (person_id: number, suppressionId: number) =>
                                    `/people/${person_id}/exposures/suppressions/${suppressionId}`,
                            },
                            custom_removal: (person_id: number) => `/people/${person_id}/exposures/custom-removal`,
                        },
                        breaches: {
                            list: (person_id: number) => `/people/${person_id}/breaches`,
                            scan: (person_id: number) => `/people/${person_id}/breaches/scan`,
                        },
                        // Shadow Identity digital footprint (replaces the breach list).
                        // Computed live, never stored: the snapshot lives in a session-length
                        // backend cache, and only vault adds persist.
                        footprint: {
                            /** GET — pure read of the current session snapshot; never starts a scan. */
                            show: (person_id: number) => `/people/${person_id}/footprint`,
                            /** POST — run a scan wave at a depth, with the session's switched-off seeds. */
                            scan: (person_id: number) => `/people/${person_id}/footprint/scan`,
                            /** POST — live, session-scoped view of the records behind a snapshot value (metered source call). */
                            records: (person_id: number) => `/people/${person_id}/footprint/records`,
                            /** POST — add picked snapshot values to the person's identity vault. */
                            addToVault: (person_id: number) => `/people/${person_id}/footprint/add-to-vault`,
                            /** POST — cleartext behind ONE masked credential, addressed by its snapshot handle. */
                            sensitiveReveal: (person_id: number) => `/people/${person_id}/footprint/sensitive-reveal`,
                        },
                        companies: {
                            get_company: (company_id: number) => `/companies/${company_id}`,
                            search: (query: string, per_page: number, page: number, type: string) =>
                                `/companies?per_page=${per_page}&page=${page}&search=${query}&type=${type}`,
                            untrust_company: (person_id: number, company_id: number) =>
                                `/people/${person_id}/companies/${company_id}/untrust`,
                            exposures: (person_id: number, company_id: number) =>
                                `/people/${person_id}/companies/${company_id}/exposures`,
                        },

                        searchResults: {
                            /**
                             * GET /v3/people/{person_id}/search-results
                             * @param person_id - The person ID
                             * @param status - Filter by status: 'active' | 'removed' | 'ignored'
                             * @param after_key - The "after key" AKA page identifier
                             */
                            list: (person_id: number, status?: string, after_key?: string | null) => {
                                const params = new URLSearchParams(
                                    Object.entries({ status, after: after_key }).filter((e): e is [string, string] => !!e[1])
                                ).toString();

                                return `/people/${person_id}/search-results${params ? `?${params}` : ''}`;
                            },
                            /**
                             * GET /v3/people/{person_id}/search-results/statistics
                             * Get statistics about search results
                             */
                            statistics: (person_id: number) => `/people/${person_id}/search-results/statistics`,
                            /**
                             * PUT /v3/people/{person_id}/search-results/{urlHash}
                             * @param person_id - The person ID
                             * @param urlHash - The URL hash of the search result
                             */
                            update: (person_id: number, urlHash: string) => `/people/${person_id}/search-results/${urlHash}`,
                            /**
                             * POST /v3/people/{person_id}/search-results/scan
                             * Trigger a new scan (runs as background job)
                             */
                            scan: (person_id: number) => `/people/${person_id}/search-results/scan`,
                            /**
                             * GET /v3/people/{person_id}/search-results/scan/status
                             * Get the current scan status
                             */
                            scanStatus: (person_id: number) => `/people/${person_id}/search-results/scan/status`,
                            /**
                             * POST /v3/people/{person_id}/search-results/mark-as-exposure
                             * Create an exposure from a search result
                             */
                            markAsExposure: (person_id: number) => `/people/${person_id}/search-results/mark-as-exposure`,
                        },

                        contacts: {
                            get_contacts: (person_id: number) => `/people/${person_id}/contacts?per_page=10000&blocked=false`,
                            create_contact: (person_id: number) => `/people/${person_id}/contacts`,
                            /**
                             * PUT /v3/people/{person_id}/contacts/{contact_id}
                             * REPLACES the contact: the emails and phone numbers sent are the
                             * whole list afterwards, and `name` is required on every call.
                             */
                            update_contact: (person_id: number, contact_id: number) => `/people/${person_id}/contacts/${contact_id}`,
                            delete: (person_id: number, contact_id: number) => `/people/${person_id}/contacts/${contact_id}`,
                            minor_eligibility: (person_id: number) => `/people/${person_id}/contacts/minor-eligibility`,
                            sign_minor_consent: (person_id: number) => `/people/${person_id}/contacts/minor-consent`,
                            create_minor: (person_id: number) => `/people/${person_id}/contacts/minor`,
                        },

                        //pb university
                        university: {
                            get_statistics: (person_id: number) => `/people/${person_id}/university/statistics`,
                            get_module_info: (person_id: number, module: string) =>
                                `/people/${person_id}/university/modules/${module}`,
                            update_lesson_progress: (person_id: number, module: string, lesson_id: number) =>
                                `/people/${person_id}/university/modules/${module}/lesson/${lesson_id}`,
                        },

                        licenses: {
                            update_license: (person_id: number, license_id: number) =>
                                `/people/${person_id}/licenses/${license_id}`,
                            change_license: (person_id: number, license_id: number) =>
                                `/people/${person_id}/licenses/${license_id}/change`,
                            refund: (person_id: number, license_id: number) => `/people/${person_id}/licenses/${license_id}`,
                        },
                        akas: {
                            create: (person: number) => `/people/${person}/akas`,
                            update: (person: number, akaId: number) => `/people/${person}/akas/${akaId}`,
                            delete: (person: number, akaId: number) => `/people/${person}/akas/${akaId}`,
                        },
                        emails: {
                            sendVerification: () => `/me/emails/verify`,
                            create: (person: number) => `/people/${person}/emails`,
                            delete: (person: number, email: number) => `/people/${person}/emails/${email}`,
                            update: (person: number, email: number) => `/people/${person}/emails/${email}`,
                        },
                        address: {
                            create: (person: number) => `/people/${person}/addresses`,
                            delete: (person: number, address: number) => `/people/${person}/addresses/${address}`,
                            update: (person: number, address: number) => `/people/${person}/addresses/${address}`,
                        },
                        phones: {
                            create: (person: number) => `/people/${person}/phones`,
                            delete: (person: number, phone: number) => `/people/${person}/phones/${phone}`,
                            sendVerification: (person: number) => `/people/${person}/phones/verify`,
                            update: (person: number, phone: number) => `/people/${person}/phones/${phone}`,
                        },
                        options: {
                            get: (person: number) => `/people/${person}/options`,
                            update: (person: number) => `/people/${person}/options`,
                        },
                        marketingOptOuts: {
                            get: (person: number) => `/people/${person}/marketing-optouts`,
                            update: (person: number) => `/people/${person}/marketing-optouts`,
                        },
                        communicationPreferences: {
                            get: (person: number) => `/people/${person}/communication-preferences`,
                            update: (person: number) => `/people/${person}/communication-preferences`,
                        },
                        poa: {
                            sign: (person: number) => `/people/${person}/lpoa`,
                            retract: (person: number) => `/people/${person}/lpoa`,
                        },
                        secureFiles: {
                            list: (person: number) => `/people/${person}/secure-files`,
                            reserve: (person: number) => `/people/${person}/secure-files`,
                            confirm: (person: number, file: number) => `/people/${person}/secure-files/${file}/confirm`,
                            remove: (person: number, file: number) => `/people/${person}/secure-files/${file}`,
                        },
                    },
                    public: {
                        companies: {
                            search: (
                                query: string,
                                per_page: number,
                                page: number,
                                type: string,
                                trust: boolean,
                                license?: string
                            ) => {
                                let url = `/public/companies?per_page=${per_page}&page=${page}&search=${query}&type=${type}&trust_page=${trust}`;
                                if (license) {
                                    url += `&license=${license}`;
                                }
                                return url;
                            },
                            advisories: (company_id: number) => `/public/companies/${company_id}/advisories`,
                        },
                    },
                    // Free licenses. The `me` routes answer for the signed-in member, whoever is on
                    // screen. `public` reads what a claim link offers without a session, so the
                    // token in the address is all it is given.
                    licenseGifts: {
                        list: () => `/me/license-gifts`,
                        invitations: {
                            create: () => `/me/license-gifts/invitations`,
                            resend: (gift: number) => `/me/license-gifts/invitations/${gift}/resend`,
                        },
                        shareLink: () => `/me/license-gifts/share-link`,
                        claim: () => `/me/license-gifts/claim`,
                        public: (token: string) => `/public/license-gifts/${encodeURIComponent(token)}`,
                    },
                    countries: {
                        lookup: (id: number) => `/countries/${id}`,
                    },
                    auth: {
                        passkeys: {
                            registerOptions: () => `/auth/passkeys/register-options`,
                            register: () => `/auth/passkeys/register`,
                        },
                    },
                },
            },
        },
    };
});
